import * as React from 'react';

import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, FormControlLabel, Paper, Radio, Stack, Tooltip, Typography } from '@mui/material';
import { AlertProps } from '@mui/material/Alert';
import Grid from '@mui/material/Grid2';
import ExpandMore from '@mui/icons-material/ExpandMore';
import TaskAlt from '@mui/icons-material/TaskAlt';
import CancelRounded from '@mui/icons-material/CancelRounded';

import SvgIconConsumer from '../../SVGIcons/SVGIconConsumer';
import SVGIconProvider from '../../SVGIcons/SVGIconProvider';

import { Page } from '../../subs/Page';
import { WorkshopProduct } from '../../types/WorkshopProducts';
import { ProductInterface } from "../../types/ProductInterface";
import { WorkshopProductCombination } from '../../types/WorkshopProductCombination';
import { WorkshopTestCase, TestSupportStatus } from "../../types/WorkshopTestCases";
import { TestCombination } from '../../types/TestCombinations';
import { Product } from '../../types/Product';
import { Interface } from '../../types/Interface';
import { TestCase } from '../../types/TestCase';
import { groupBy, copyArrayWithIndexChanged } from '../../Utils';
import { httpPost } from '../../Web';
import { useSnackbar } from '../../../contexts/SnackBarContext';
import { InputCompletionChip } from "./InputCompletionChip";

// global theming ruins the radio, disable it here while still inheriting dark-mode palette/applyStyles from the ambient theme
const useNoRadioTheme = () => {
    const outerTheme = useTheme();
    return React.useMemo(() => createTheme(outerTheme, {
        components: {
            MuiRadio: {
                styleOverrides: {
                    root: {
                    },
                },
            }
        }
    }), [outerTheme]);
};

//testCombinations?: TestCombination[],

export const WSTestCasesConfig = ({ product, testCombination, workshopProduct, workshopTestcases, wsProdCombination, 
    testCases, wsTestCaseHandler, interfaces, productInterfaces }:
    {
        workshopProduct: WorkshopProduct,
        wsProdCombination: WorkshopProductCombination,
        testCombination: TestCombination | undefined,
        product: Product,
        workshopTestcases: WorkshopTestCase[],
        testCases: Map<number, TestCase[]> | undefined,
        interfaces: Interface[],
        productInterfaces: ProductInterface[],
        wsTestCaseHandler: (wstc: WorkshopTestCase[]) => void
    }) => {
    const { showSnackbar } = useSnackbar();

    const showUserFeedback = React.useCallback((msg: string, severity: AlertProps['severity'] = 'error') => {
        showSnackbar(msg, severity);
    }, [showSnackbar]);

    let changesDisabled: boolean = false;

    if (workshopProduct === undefined) changesDisabled = true;
    else {
        if (wsProdCombination.isConsumer && wsProdCombination.consumerReadyToTest) changesDisabled = true;
        if (wsProdCombination.isProvider && wsProdCombination.providerReadyToTest) changesDisabled = true;
    }

    const setTestCaseConfig = async (testCaseIds: number[], supported: TestSupportStatus) => {

        let updatedWSTestCaes: Promise<any>[] = [];
        let abortController = new AbortController();

        if (changesDisabled) {
            showUserFeedback('Changes are disabled while the product is marked as \'ready to test\'.');
            return;
        }

        testCaseIds.forEach(tc => {
            let wstc = workshopTestcases.find(wstc => wstc.testCaseId === tc);
            if (!wstc) {//testCombinationId
                wstc = {
                    id: 0, testCaseId: tc, workshopProductId: workshopProduct.id, testCombinationId: wsProdCombination.testCombinationId,
                    supportStatus: supported
                } as WorkshopTestCase;
            }
            else {
                wstc.supportStatus = supported;
            }
            updatedWSTestCaes.push(httpPost(`/api/workshopproduct/workshoptestcase/update`, wstc, abortController));
        })

        const responses = await Promise.all(updatedWSTestCaes);
        const someFailed = responses.find(response => response.failed);
        if ((someFailed)) {
            showUserFeedback(someFailed.errorText);
            return;
        }

        let workshopTestCasesLoc = workshopTestcases;

        responses.forEach(response => {
            let workshopTestCase = workshopTestCasesLoc.find(wstc => wstc.id === response.result.id);
            if (!workshopTestCase) {
                workshopTestCasesLoc.push(response.result);
            }
            else {
                workshopTestCasesLoc = copyArrayWithIndexChanged(workshopTestCasesLoc, workshopTestCasesLoc.findIndex(wstc => wstc.id === response.result.id), response.result);
            }
        });

        wsTestCaseHandler(workshopTestCasesLoc);
        showUserFeedback('Testcase support saved.', 'success');
    }

    return <Page heading={`Supported Test Cases for ${product?.name} ${workshopProduct?.productVersion ?? ""}`}>
        {[product ?
            <InterfaceTCAccordion interfaces={interfaces} productInterfaces={productInterfaces} testCases={testCases} workshopTestcases={workshopTestcases}
                testCombination={testCombination}
                wsProdCombination={wsProdCombination} setTestCaseConfig={setTestCaseConfig} /> : <></>]}
    </Page>
}

const InterfaceTCAccordion = ({ interfaces, productInterfaces, testCases, wsProdCombination, testCombination, setTestCaseConfig, workshopTestcases }:
    {
        interfaces: Interface[], testCases: Map<number, TestCase[]> | undefined, setTestCaseConfig: (utcId: number[], support: TestSupportStatus) => void,
        wsProdCombination: WorkshopProductCombination,
        testCombination: TestCombination | undefined,
        workshopTestcases: WorkshopTestCase[], productInterfaces: ProductInterface[]
    }) => {

    return <Box>
        {(
            productInterfaces.map(wspi => wspi.supportedByProduct ? 
                <Accordion key={wspi.interfaceId} defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore/>}>
                        <Typography variant='bold'>{interfaces?.find(intf => intf.id === wspi.interfaceId)?.name ?? 'Error - interface not found'}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {
                            <TestCaseAccordion key={wspi.interfaceId} setTestCaseConfig={setTestCaseConfig} workshopTestcases={workshopTestcases} 
                                testCombination={testCombination}
                                testCases={testCases?.get(wspi.interfaceId) ?? []} wsProdCombination={wsProdCombination} />
                    }
                    </AccordionDetails>
                </Accordion>
                : <></>
            )
        )}
    </Box>
}

const TestCaseAccordion = ({ testCases, setTestCaseConfig, testCombination, workshopTestcases, wsProdCombination }:
    {
        testCases: TestCase[], setTestCaseConfig: (utcId: number[], support: TestSupportStatus) => void,
        testCombination: TestCombination | undefined,
        workshopTestcases: WorkshopTestCase[], wsProdCombination: WorkshopProductCombination
    }) => {
    const groupedTestCases = groupBy(testCases ?? [], ifaceTestCase => ifaceTestCase.group);

    return <>{
        <>
            {
                Object.keys(groupedTestCases as { [index: string]: TestCase[] })
                    .map(group => <>
                        {<TestCaseAccordionGroup key={group} testCases={(groupedTestCases as { [index: string]: TestCase[] })[group]}
                            testCombination={testCombination}
                            workshopTestcases={workshopTestcases.filter(wstc => ((groupedTestCases as { [index: string]: TestCase[] })[group]).find(gtc => gtc.id === wstc.testCaseId))}
                            wsProdCombination={wsProdCombination} setTestCaseConfig={setTestCaseConfig} group={group} />}
                    </>
                    )
            }
        </>
    }
    </>
}

const TestCaseAccordionGroup = ({ testCases, setTestCaseConfig, workshopTestcases, wsProdCombination, testCombination, group }:
    {
        testCases: TestCase[], setTestCaseConfig: (utcId: number[], support: TestSupportStatus) => void,
        wsProdCombination: WorkshopProductCombination,
        testCombination: TestCombination | undefined,
        workshopTestcases: WorkshopTestCase[], group: string
    }) => {
    const [groupComplete, setgroupComplete] = React.useState<boolean>(false);

    React.useEffect(() => {
        let undefTC = testCases.filter(tc => !workshopTestcases.find(wstc => wstc.testCaseId === tc.id));
        let undecided = workshopTestcases.filter(wstc => wstc.supportStatus === TestSupportStatus.Undecided);

        setgroupComplete(undefTC.length === 0 && undecided.length === 0);
    }, [workshopTestcases, testCases]);

    return <>
        {
            <AccordionDetails>
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Stack width={'100%'} direction={'row'} justifyContent={'space-between'}>
                        {group}
                            <InputCompletionChip completed={groupComplete} />
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                        {<TestCasesRadioGroup testCases={testCases}
                            testCombination={testCombination}
                                        workshopTestcases={workshopTestcases}
                                        wsProdCombination={wsProdCombination}
                                        setTestCaseConfig={setTestCaseConfig} />}
                    </AccordionDetails>
                </Accordion>
            </AccordionDetails>     
        }
    </>
}

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: '#fff',
    ...theme.typography.body2,
    padding: 10,
    textAlign: 'left',
    minHeight: 20,
    maxHeight: 60,
    scrollBehavior: 'auto',
    color: theme.palette.text.primary,
    ...theme.applyStyles('dark', {
        backgroundColor: '#1A2027',
    }),
}));

const Item2 = styled(Paper)(({ theme }) => ({
    backgroundColor: '#fff',
    ...theme.typography.body2,
    padding: 10,
    textAlign: 'right',
    fontWeight: 'bold',
    minHeight: 30,
    maxHeight: 30,
    scrollBehavior: 'auto',
    color: theme.palette.text.primary,
    ...theme.applyStyles('dark', {
        backgroundColor: '#1A2027',
    }),
}));

const TestCasesRadioGroup = ({ testCases, setTestCaseConfig, wsProdCombination, testCombination, workshopTestcases }:
    {
        testCases: TestCase[], setTestCaseConfig: (utcId: number[], support: TestSupportStatus) => void,
        wsProdCombination: WorkshopProductCombination,
        testCombination: TestCombination | undefined,
        workshopTestcases: WorkshopTestCase[]
    }) => {
    const noRadioTheme = useNoRadioTheme();

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const supported = (event.target as HTMLInputElement).value;
        let supportStatus: TestSupportStatus = TestSupportStatus.Undecided;
        switch (supported) {
            case 'client_supported':
                supportStatus = TestSupportStatus.ClientSupported;
                break;
            case 'server_supported':
                supportStatus = TestSupportStatus.ServerSupported;
                break;
            case 'supported':
                supportStatus = TestSupportStatus.Supported;
                break;
            case 'unsupported':
                supportStatus = TestSupportStatus.Unsupported;
                break;
        }
        if (!wsProdCombination.isConsumer && supportStatus === TestSupportStatus.ClientSupported) return;
        if (!wsProdCombination.isProvider && supportStatus === TestSupportStatus.ServerSupported) return;
        setTestCaseConfig(testCases.map(testCase => testCase.id), supportStatus);
    };

    return <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Grid container spacing={{ xs: 2, md: 5 }}>
            <ThemeProvider theme={noRadioTheme}>
                <Grid size={(wsProdCombination.isConsumer && wsProdCombination.isProvider ? 8 : 10)}>
                    <Item2 elevation={0} >Test Cases - mark all</Item2>
                </Grid>

                {wsProdCombination.isConsumer && wsProdCombination.isProvider && <Grid size={1}>
                    <Tooltip title={`Mark all as only supported on ${testCombination?.combinationConsumer ?? 'client'} side`}>
                        <FormControlLabel value="client_supported"
                            control={<Radio
                                checked={false}
                                disabled={!wsProdCombination.isConsumer}
                                color="success"
                                onChange={handleChange}
                                icon=<SvgIconConsumer />
                            />}
                            label="" labelPlacement='start' />
                    </Tooltip>
                </Grid>}

                {wsProdCombination.isConsumer && wsProdCombination.isProvider && <Grid size={1}>
                    <Tooltip title={`Mark all as only supported on ${testCombination?.combinationProvider ?? 'server'} side`}>
                        <FormControlLabel value="server_supported"
                            control={<Radio
                                checked={false}
                                disabled={!wsProdCombination.isProvider}
                                color="success"
                                onChange={handleChange}
                                icon=<SVGIconProvider />
                            />}
                            label="" labelPlacement='start' />
                    </Tooltip>
                </Grid>}

                <Grid size={1}>
                    <Tooltip title={"Mark all as " + (wsProdCombination.isConsumer && wsProdCombination.isProvider ? "fully supported" : "supported")}>
                        <FormControlLabel value="supported"
                            control={<Radio
                                checked={false}
                                color="success"
                                onChange={handleChange}
                                icon=<TaskAlt />
                            />}
                            label="" labelPlacement='start' />
                    </Tooltip>
                </Grid>
                <Grid size={1}>
                    <Tooltip title="Mark all as not supported">
                        <FormControlLabel value="unsupported"
                            control={<Radio
                                checked={false}
                                color="error"
                                onChange={handleChange}
                                icon=<CancelRounded />
                            />}
                            label="" />
                    </Tooltip>
                </Grid>
            </ThemeProvider>
            {testCases.map(testCase =>
                <TestCaseRadioGroup key={testCase.id} testCase={testCase} setTestCaseConfig={setTestCaseConfig} wsProdCombination={wsProdCombination}
                    testCombination={testCombination}
                    workshopTestcase={workshopTestcases.find(wstc => wstc.testCaseId === testCase.id)} />
            )}
        </Grid>
    </div>
}

const TestCaseRadioGroup = ({ testCase, workshopTestcase, wsProdCombination, testCombination, setTestCaseConfig }:
    {
        testCase: TestCase, setTestCaseConfig: (utcId: number[], support: TestSupportStatus) => void,
        workshopTestcase: WorkshopTestCase | undefined, wsProdCombination: WorkshopProductCombination,
        testCombination: TestCombination | undefined
    }) => {
    const [expanded, setExpanded] = React.useState<boolean>(false);
    const noRadioTheme = useNoRadioTheme();

    const handleAccordionChange =
        () => (event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded);
        };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const supported = (event.target as HTMLInputElement).value;
        let supportStatus: TestSupportStatus = TestSupportStatus.Undecided;
        switch (supported) {
            case 'client_supported':
                supportStatus = TestSupportStatus.ClientSupported;
                break;
            case 'server_supported':
                supportStatus = TestSupportStatus.ServerSupported;
                break;
            case 'supported':
                supportStatus = TestSupportStatus.Supported;
                break;
            case 'unsupported':
                supportStatus = TestSupportStatus.Unsupported;
                break;
        }
        if (!wsProdCombination.isConsumer && supportStatus === TestSupportStatus.ClientSupported) return;
        if (!wsProdCombination.isProvider && supportStatus === TestSupportStatus.ServerSupported) return;
        setTestCaseConfig([testCase.id], supportStatus);
       };

    return <>
        <Grid size={(wsProdCombination.isConsumer && wsProdCombination.isProvider ? 8 : 10)}>
                {testCase.description ?
                <AccordionDetails>
                    <Accordion defaultExpanded={false} onChange={handleAccordionChange()}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Stack width={'100%'} spacing={10} direction={'row'} justifyContent={'space-between'}>
                                <Box width={'90%'}> <Item elevation={0}>{testCase.name}</Item> </Box>
                                <Divider orientation="vertical" flexItem />
                                <Typography variant='subscript'>{expanded ? 'Hide description' : 'Show description'}</Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Divider />
                            <Item elevation={0}>{testCase.description}</Item>
                            <Divider />
                        </AccordionDetails>
                    </Accordion>
                </AccordionDetails>
                :
                <Item elevation={3}>{testCase.name}</Item>}
        </Grid>
        <ThemeProvider theme={noRadioTheme}>
            {wsProdCombination.isConsumer && wsProdCombination.isProvider && <Grid size={1}>
                <Tooltip title={`Only supported on ${testCombination?.combinationConsumer ?? 'client'} side`}>
                    <FormControlLabel value="client_supported"
                        control={<Radio
                            checked={workshopTestcase?.supportStatus === TestSupportStatus.ClientSupported}
                            disabled={!wsProdCombination.isConsumer}
                            color="success"
                            onChange={handleChange}
                        />}
                        label="" labelPlacement='start' />
                </Tooltip>
            </Grid>}

            {wsProdCombination.isConsumer && wsProdCombination.isProvider && <Grid size={1}>
                <Tooltip title={`Only supported on ${testCombination?.combinationProvider ?? 'server'} side`}>
                    <FormControlLabel value="server_supported"
                        control={<Radio
                            checked={workshopTestcase?.supportStatus === TestSupportStatus.ServerSupported}
                            disabled={!wsProdCombination.isProvider}
                            color="success"
                            onChange={handleChange}
                        />}
                        label="" labelPlacement='start' />
                </Tooltip>
            </Grid>
            }
            <Grid size={1}>
                <Tooltip title={wsProdCombination.isConsumer && wsProdCombination.isProvider ? "Fully supported" : "Supported"}>
                        <FormControlLabel value="supported"
                            control={<Radio
                                checked={((workshopTestcase?.supportStatus === TestSupportStatus.Supported)
                                    || (!wsProdCombination.isConsumer && workshopTestcase?.supportStatus === TestSupportStatus.ServerSupported)
                                    || (!wsProdCombination.isProvider && workshopTestcase?.supportStatus === TestSupportStatus.ClientSupported))}
                                color="success"
                                onChange={handleChange}
                            />}
                        label="" labelPlacement='start' />
                </Tooltip>
            </Grid>
            <Grid size={1}>
                <Tooltip title={"Not supported"}>
                    <FormControlLabel value="unsupported"
                        control={<Radio
                            checked={((workshopTestcase?.supportStatus === TestSupportStatus.Unsupported)
                                || (wsProdCombination.isConsumer && workshopTestcase?.supportStatus === TestSupportStatus.ServerSupported)
                                || (wsProdCombination.isProvider && workshopTestcase?.supportStatus === TestSupportStatus.ClientSupported))}
                            color="error"
                            onChange={handleChange}
                        />}
                        label="" />
                </Tooltip>
            </Grid>
        </ThemeProvider>
    </>
}
